import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./App.css";
import "./WelcomePage.css";
import { clearToken, getToken, handleUnauthorizedResponse } from "./authStorage";

const API_BASE = process.env.REACT_APP_API_BASE || "/api";
const GENERIC_SUPPORT_ERROR = "Oops failed, Please contact support team";
const MAX_PDFS = 1;
const MAX_PDF_PAGES = 1500;
const NOTIFICATION_TITLE = "Excel generated";
const SUPPORTED_PROCESSING_BANKS = new Set([
  "Barclays Business",
  "Cooperative Bank Business",
  "Halifax",
  "Monzo",
  "Revolut",
  "Revolut Business",
  "Starling",
  "TSB",
  "Tide",
  "Virgin Money",
  "Bank of Scotland",
]);

const UPLOAD_PDF_ICON_URL = "https://cdn-icons-png.flaticon.com/512/5868/5868106.png";

const BANK_OPTIONS = [
  { name: "Bank of Scotland" },
  { name: "Barclays Business" },
  { name: "Capital" },
  { name: "Cooperative Bank Business" },
  { name: "Halifax" },
  { name: "HSBC business" },
  { name: "Lloyds" },
  { name: "Monzo" },
  { name: "NatWest" },
  { name: "Revolut" },
  { name: "Revolut Business" },
  { name: "Royal Bank of Scotland" },
  { name: "Santander" },
  { name: "Starling" },
  { name: "Tesco" },
  { name: "Tide" },
  { name: "TSB" },
  { name: "Virgin Money" },
];

function formatFileSize(bytes) {
  const numericBytes = Number(bytes || 0);
  if (!numericBytes) {
    return "Upload PDF";
  }
  if (numericBytes < 1024 * 1024) {
    return `${Math.max(1, Math.round(numericBytes / 1024))} KB`;
  }
  return `${(numericBytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatPageCount(count) {
  const numericCount = Number(count || 0);
  if (!numericCount) {
    return "Upload PDF";
  }
  return `${numericCount} ${numericCount === 1 ? "page" : "pages"}`;
}

function supportsSystemNotifications() {
  return typeof window !== "undefined" && "Notification" in window;
}

function WelcomePage() {
  const fileInputRef = useRef(null);
  const xlsxInputRef = useRef(null);
  const currentJobIdRef = useRef(null);
  const currentJobLocalIdRef = useRef(null);
  const activeRequestControllerRef = useRef(null);
  const cancelRequestedRef = useRef(false);
  const rowCancelRequestedRef = useRef(new Set());
  const removedJobIdsRef = useRef(new Set());
  const [tipsOpen, setTipsOpen] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [, setStatusMessage] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedXlsxFile, setSelectedXlsxFile] = useState(null);
  const [filterLoading, setFilterLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [activePreviewId, setActivePreviewId] = useState(null);
  const [selectedBankName, setSelectedBankName] = useState("");
  const [bankSearch, setBankSearch] = useState("");

  const requestNotificationPermission = async () => {
    if (!supportsSystemNotifications()) {
      return "unsupported";
    }

    if (Notification.permission === "granted") {
      return "granted";
    }

    if (Notification.permission === "denied") {
      return "denied";
    }

    try {
      return await Notification.requestPermission();
    } catch (error) {
      return "denied";
    }
  };

  const showCompletionNotification = async (job) => {
    const permission = await requestNotificationPermission();
    if (permission !== "granted") {
      return;
    }

    const notification = new Notification(NOTIFICATION_TITLE, {
      body: `${job.fileName || "Your file"} is ready to download.`,
      requireInteraction: true,
      tag: `excel-generated-${job.localId}`,
    });

    notification.onclick = () => {
      window.focus();
      setStatusMessage(`${job.fileName || "Your file"} is ready to download.`);
      notification.close();
    };
  };

  const abortActiveBrowserRequests = () => {
    if (activeRequestControllerRef.current) {
      activeRequestControllerRef.current.abort();
      activeRequestControllerRef.current = null;
    }
  };

  const fireAndForgetCancelCurrentJob = () => {
    const token = getToken();
    const jobId = currentJobIdRef.current;
    if (!token || !jobId) return;

    try {
      fetch(`${API_BASE}/jobs/${jobId}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        keepalive: true,
      })
        .then(handleUnauthorizedResponse)
        .catch(() => {});
    } catch (error) {
      // Best-effort on page close/navigation only.
    }
  };

  const handleLogout = async () => {
    if (loading) {
      cancelRequestedRef.current = true;
      abortActiveBrowserRequests();
      fireAndForgetCancelCurrentJob();
    }
    clearToken();
    window.location.replace("/login");
  };

  const updateJobEntry = (localId, updater) => {
    setJobs((currentJobs) =>
      currentJobs.map((job) => {
        if (job.localId !== localId) {
          return job;
        }
        return typeof updater === "function" ? updater(job) : { ...job, ...updater };
      })
    );
  };

  const removeJobEntry = (localId) => {
    setJobs((currentJobs) => {
      const nextJobs = currentJobs.filter((job) => job.localId !== localId);
      setSelectedFiles(nextJobs.map((job) => job.file));
      return nextJobs;
    });
    if (activePreviewId === localId) {
      setActivePreviewId(null);
    }
  };

  const throwIfUnauthorized = (response) => {
    if (handleUnauthorizedResponse(response)) {
      throw new Error("Session expired. Please sign in again.");
    }
  };

  const readMetadata = async (file, token, signal) => {
    const fd = new FormData();
    fd.append("file", file);

    const res = await fetch(`${API_BASE}/statement-metadata`, {
      method: "POST",
      body: fd,
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });

    throwIfUnauthorized(res);
    if (!res.ok) {
      throw new Error("Could not read PDF metadata.");
    }

    const data = await res.json();
    return data.pages || 0;
  };

  const createJob = async (file, totalPages, bankName, token, signal) => {
    const fd = new FormData();
    fd.append("bank_name", bankName);
    fd.append("large_txn_threshold", "10");
    fd.append("total_pages", String(totalPages || 0));
    fd.append("original_filename", file.name);

    const res = await fetch(`${API_BASE}/jobs`, {
      method: "POST",
      body: fd,
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });

    throwIfUnauthorized(res);
    if (!res.ok) {
      throw new Error(await res.text());
    }

    return res.json();
  };

  const requestCancelJob = async (jobId, token, options = {}) => {
    if (!jobId) return;
    const res = await fetch(`${API_BASE}/jobs/${jobId}/cancel`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
      keepalive: Boolean(options.keepalive),
      signal: options.signal,
    });
    throwIfUnauthorized(res);
  };

  const uploadToS3 = async (uploadUrl, file, signal) => {
    const res = await fetch(uploadUrl, {
      method: "PUT",
      headers: { "Content-Type": "application/pdf" },
      body: file,
      signal,
    });

    if (!res.ok) {
      throw new Error("S3 upload failed");
    }
  };

  const fetchPreview = async (jobId, token, signal) => {
    const res = await fetch(`${API_BASE}/jobs/${jobId}/preview?max_rows=5`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });

    throwIfUnauthorized(res);
    if (!res.ok) {
      throw new Error(await res.text());
    }

    return res.json();
  };

  const fetchDownloadUrl = async (jobId, token, signal) => {
    const res = await fetch(`${API_BASE}/jobs/${jobId}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });

    throwIfUnauthorized(res);
    if (!res.ok) {
      throw new Error(await res.text());
    }

    const data = await res.json();
    return data.download_url || "";
  };

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const filenameFromContentDisposition = (contentDisposition, fallback) => {
    const match = /filename="?([^"]+)"?/i.exec(contentDisposition || "");
    return match?.[1] || fallback;
  };

  const createFilteredExcel = async (file, token) => {
    const fd = new FormData();
    fd.append("file", file);

    const res = await fetch(`${API_BASE}/filter-excel`, {
      method: "POST",
      body: fd,
      headers: { Authorization: `Bearer ${token}` },
    });

    throwIfUnauthorized(res);
    if (!res.ok) {
      throw new Error(await res.text());
    }

    const blob = await res.blob();
    const fallbackName = file.name.replace(/\.xlsx$/i, "_filtered.xlsx");
    return {
      blob,
      filename: filenameFromContentDisposition(res.headers.get("content-disposition"), fallbackName),
    };
  };

  const handleDownloadExcel = async (job) => {
    if (!job?.jobId) {
      setErrorMessage("This job is missing its download reference.");
      return;
    }

    const token = getToken();
    if (!token) {
      handleLogout();
      return;
    }

    try {
      setErrorMessage("");
      const downloadUrl = await fetchDownloadUrl(job.jobId, token);
      if (!downloadUrl) {
        throw new Error("No download URL was returned.");
      }
      window.location.assign(downloadUrl);
    } catch (error) {
      setErrorMessage(error.message || "Could not start the Excel download.");
    }
  };

  const processFile = async (jobEntry, token, fileIndex, totalFiles, bankName) => {
    const { localId, file } = jobEntry;
    const requestController = new AbortController();
    activeRequestControllerRef.current = requestController;
    try {
      currentJobLocalIdRef.current = localId;
      updateJobEntry(localId, {
        status: "reading",
        statusText: "Reading PDF metadata...",
        progress: 0,
        processedPages: 0,
        totalPages: 0,
        error: "",
      });

      const totalPages = await readMetadata(file, token, requestController.signal);
      updateJobEntry(localId, {
        totalPages,
        status: "uploading",
        statusText: `Uploading ${file.name} (${fileIndex + 1}/${totalFiles})...`,
        progress: 0,
        processedPages: 0,
      });

      const job = await createJob(file, totalPages, bankName, token, requestController.signal);
      currentJobIdRef.current = job.job_id;
      updateJobEntry(localId, { jobId: job.job_id });
      await uploadToS3(job.upload_url, file, requestController.signal);

      updateJobEntry(localId, {
        status: "queued",
        statusText: "Upload complete. Waiting to queue job...",
        progress: 5,
      });

      const enqueueRes = await fetch(`${API_BASE}/jobs/${job.job_id}/enqueue`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        signal: requestController.signal,
      });

      throwIfUnauthorized(enqueueRes);
      if (!enqueueRes.ok) {
        throw new Error(`Failed to queue job (${enqueueRes.status})`);
      }

      updateJobEntry(localId, {
        status: "queued",
        statusText: "Queued for processing...",
      });

      while (true) {
        if (cancelRequestedRef.current) {
          throw new Error("Conversion cancelled");
        }

        const res = await fetch(`${API_BASE}/jobs/${job.job_id}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: requestController.signal,
        });

        throwIfUnauthorized(res);
        if (!res.ok) {
          throw new Error(`Polling failed (${res.status})`);
        }

        const data = await res.json();
        const backendStatus = data.status || "";
        const isQueued = backendStatus === "ENQUEUED";
        const isCompleted = backendStatus === "COMPLETED";
        const isFailed = backendStatus === "FAILED";
        const isCancelled = backendStatus === "CANCELLED";

        updateJobEntry(localId, {
          status: isCompleted ? "completed" : isQueued ? "queued" : "processing",
          statusText: isQueued ? "Queued for processing..." : "Converting statement to Excel...",
          progress: Math.max(0, Math.round(data.progress || 0)),
          processedPages: data.processed_pages || 0,
          totalPages: data.total_pages || totalPages || 0,
        });

        if (isCompleted) {
          updateJobEntry(localId, {
            status: "finalizing",
            statusText: "Loading preview...",
            progress: 100,
            processedPages: data.total_pages || totalPages || 0,
          });
          break;
        }

        if (isFailed) {
          throw new Error(data.error || "Job failed during processing");
        }

        if (isCancelled) {
          throw new Error("Conversion cancelled");
        }

        await new Promise((resolve) => setTimeout(resolve, 1500));
      }

      const preview = await fetchPreview(job.job_id, token, requestController.signal);
      updateJobEntry(localId, {
        status: "completed",
        statusText: "Excel ready.",
        progress: 100,
        processedPages: totalPages,
        preview: {
          headers: preview.headers || [],
          rows: preview.rows || [],
          sheetName: preview.sheet_name || "",
          fileName: preview.filename || file.name,
          downloadUrl: preview.download_url || "",
          truncated: Boolean(preview.truncated),
        },
      });
      await showCompletionNotification({
        fileName: preview.filename || file.name,
        localId,
      });
      currentJobIdRef.current = null;
    } catch (error) {
      const message = cancelRequestedRef.current || error.name === "AbortError"
        ? "Conversion cancelled"
        : (error.message || GENERIC_SUPPORT_ERROR);
      const rowCancelled = rowCancelRequestedRef.current.has(localId);
      updateJobEntry(localId, {
        status: message === "Conversion cancelled" ? "cancelled" : "failed",
        statusText: message === "Conversion cancelled" ? "Conversion cancelled." : "Processing failed.",
        error: message,
      });
      currentJobIdRef.current = null;
      currentJobLocalIdRef.current = null;
      if (rowCancelled) {
        rowCancelRequestedRef.current.delete(localId);
        removeJobEntry(localId);
        return;
      }
      throw error;
    } finally {
      if (activeRequestControllerRef.current === requestController) {
        activeRequestControllerRef.current = null;
      }
      if (currentJobLocalIdRef.current === localId) {
        currentJobLocalIdRef.current = null;
      }
    }
  };

  const handleFiles = async (files) => {
    if (!files.length) {
      setErrorMessage("Please select at least one PDF.");
      return;
    }

    if (files.length > MAX_PDFS) {
      setErrorMessage("You can upload only 1 PDF file.");
      return;
    }

    const [file] = files;
    const token = getToken();
    const requestController = new AbortController();
    activeRequestControllerRef.current = requestController;

    let totalPages = 0;
    try {
      setErrorMessage("");
      setStatusMessage("Reading PDF metadata...");
      totalPages = await readMetadata(file, token, requestController.signal);
    } catch (error) {
      const message = error.name === "AbortError"
        ? "PDF metadata check cancelled."
        : (error.message || "Could not read PDF metadata.");
      setStatusMessage("");
      setErrorMessage(message);
      return;
    } finally {
      if (activeRequestControllerRef.current === requestController) {
        activeRequestControllerRef.current = null;
      }
    }

    if (totalPages > MAX_PDF_PAGES) {
      setStatusMessage("");
      setErrorMessage(`${file.name} has ${totalPages} pages. Maximum allowed is ${MAX_PDF_PAGES} pages.`);
      return;
    }

    cancelRequestedRef.current = false;
    rowCancelRequestedRef.current = new Set();
    removedJobIdsRef.current = new Set();
    currentJobIdRef.current = null;
    currentJobLocalIdRef.current = null;
    setErrorMessage("");
    setStatusMessage("");
    setActivePreviewId(null);
    setSelectedFiles([file]);
    setJobs([
      {
        localId: `${file.name}-${file.size}-${Date.now()}`,
        file,
        fileName: file.name,
        status: "selected",
        statusText: "Ready to process",
        progress: 0,
        processedPages: 0,
        totalPages,
        error: "",
        jobId: "",
        preview: null,
      },
    ]);
  };

  const handleFileSelection = async (event) => {
    const files = Array.from(event.target.files || []).filter((file) =>
      file.name.toLowerCase().endsWith(".pdf")
    );
    await handleFiles(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleOpenPicker = () => {
    if (loading) return;
    setErrorMessage("");
    fileInputRef.current?.click();
  };

  const handleOpenXlsxPicker = () => {
    setErrorMessage("");
    xlsxInputRef.current?.click();
  };

  const handleXlsxSelection = (event) => {
    const file = Array.from(event.target.files || []).find((nextFile) =>
      nextFile.name.toLowerCase().endsWith(".xlsx")
    );

    if (!file) {
      setSelectedXlsxFile(null);
      setErrorMessage("Please upload an XLSX file.");
      return;
    }

    setSelectedXlsxFile(file);
    setErrorMessage("");
    setStatusMessage("");
    if (xlsxInputRef.current) {
      xlsxInputRef.current.value = "";
    }
  };

  const handleGenerateFilteredExcel = async () => {
    if (!selectedXlsxFile) {
      setErrorMessage("Please upload an XLSX file first.");
      return;
    }

    const token = getToken();
    if (!token) {
      handleLogout();
      return;
    }

    try {
      setFilterLoading(true);
      setErrorMessage("");
      setStatusMessage("Generating filter Excel sheet...");
      const { blob, filename } = await createFilteredExcel(selectedXlsxFile, token);
      downloadBlob(blob, filename);
      setStatusMessage("Filter Excel sheet generated.");
    } catch (error) {
      setErrorMessage(error.message || "Could not generate filter Excel sheet.");
      setStatusMessage("");
    } finally {
      setFilterLoading(false);
    }
  };

  const handleStartProcess = async () => {
    if (loading) {
      return;
    }

    if (!selectedBankName) {
      setErrorMessage("Please select a bank first.");
      return;
    }

    if (!SUPPORTED_PROCESSING_BANKS.has(selectedBankName)) {
      setErrorMessage(
        "Only Barclays Business, Cooperative Bank Business, Halifax, Monzo, Revolut, Revolut Business, Starling, TSB, Tide, Virgin Money and Bank of Scotland statements are supported right now."
      );
      return;
    }

    if (!jobs.length) {
      setErrorMessage("Please upload at least one PDF first.");
      return;
    }

    setErrorMessage("");
    setLoading(true);
    setStatusMessage(`Processing ${jobs.length} file${jobs.length === 1 ? "" : "s"}...`);
    cancelRequestedRef.current = false;
    await requestNotificationPermission();

    try {
      const token = getToken();
      const processingBankName = selectedBankName;
      for (let index = 0; index < jobs.length; index += 1) {
        const jobEntry = jobs[index];
        if (removedJobIdsRef.current.has(jobEntry.localId)) {
          continue;
        }
        await processFile(jobEntry, token, index, jobs.length, processingBankName);
      }
      setStatusMessage("Process complete");
    } catch (error) {
      setErrorMessage(error.message || GENERIC_SUPPORT_ERROR);
      setStatusMessage("Processing stopped before all files finished.");
    } finally {
      setLoading(false);
      currentJobIdRef.current = null;
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleCancelProcess = async () => {
    if (!loading) return;
    cancelRequestedRef.current = true;
    abortActiveBrowserRequests();
    setStatusMessage("Cancelling conversion...");

    try {
      const token = getToken();
      await requestCancelJob(currentJobIdRef.current, token);
      setStatusMessage("Conversion cancelled.");
    } catch (error) {
      setErrorMessage("Failed to cancel conversion.");
    } finally {
      setLoading(false);
      currentJobIdRef.current = null;
    }
  };

  const activePreviewJob = jobs.find((job) => job.localId === activePreviewId && job.preview);
  const completedJobs = jobs.filter((job) => job.preview);
  // Preview screen UI is commented out below by request; keep this flag for easy restore.
  // eslint-disable-next-line no-unused-vars
  const showPreviewScreen = Boolean(activePreviewJob);
  const currentProcessingJob = jobs[0] || null;
  const selectedPdfFile = selectedFiles[0] || currentProcessingJob?.file || null;
  const processingTotalPages = Number(currentProcessingJob?.totalPages || 0);
  const processingCompletedPages = Number(currentProcessingJob?.processedPages || 0);
  const processingCurrentPage = processingTotalPages
    ? Math.min(processingTotalPages, Math.max(1, processingCompletedPages))
    : 0;
  const processingStatusLabel = loading
    ? "Processing"
    : currentProcessingJob?.status === "completed"
      ? "Completed"
      : currentProcessingJob
        ? "Ready"
        : "Waiting for PDF";
  const processingPageMessage = loading && processingTotalPages
    ? `Processing page ${processingCurrentPage} of ${processingTotalPages}`
    : currentProcessingJob?.status === "completed" && processingTotalPages
      ? `Completed ${formatPageCount(processingTotalPages)}`
      : currentProcessingJob && processingTotalPages
        ? `Ready to process ${formatPageCount(processingTotalPages)}`
        : "Upload a PDF to see page progress";
  const normalizedBankSearch = bankSearch.trim().toLowerCase();
  const filteredBankOptions = normalizedBankSearch
    ? BANK_OPTIONS.filter((bank) => bank.name.toLowerCase().includes(normalizedBankSearch))
    : BANK_OPTIONS;

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!loading) {
      setDragActive(true);
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!loading) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.contains(event.relatedTarget)) {
      return;
    }
    setDragActive(false);
  };

  const handleDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    if (loading) return;

    const files = Array.from(event.dataTransfer.files || []).filter((file) =>
      file.name.toLowerCase().endsWith(".pdf")
    );
    await handleFiles(files);
  };

  return (
    <div className="app-container welcome-page">
      <main className="welcome-workspace">
        <aside className="welcome-sidebar">
          <div className="welcome-sidebar-brand">
            <Link to="/welcome" className="welcome-sidebar-brand-link">
              <span className="welcome-sidebar-brand-mark" aria-hidden="true" />
              <span className="welcome-sidebar-brand-text">StatementIQ</span>
            </Link>
          </div>

          <div className="welcome-sidebar-nav">
            <div className="welcome-sidebar-group">
              <p className="welcome-sidebar-title">Workspace</p>
              <Link to="/welcome" className="welcome-sidebar-link active">
                Convert Statements
              </Link>
            </div>

            <div className="welcome-sidebar-group">
              <p className="welcome-sidebar-title">Help &amp; Support</p>
              <Link to="/account" className="welcome-sidebar-link">
                Account
              </Link>
              <Link to="/workspace-faq" className="welcome-sidebar-link">
                FAQ
              </Link>
            </div>

            <div className="welcome-sidebar-group">
              <p className="welcome-sidebar-title">Legal</p>
              <Link to="/privacy" className="welcome-sidebar-link">
                Privacy Policy
              </Link>
              <Link to="/terms" className="welcome-sidebar-link">
                Terms of Service
              </Link>
            </div>
          </div>

          <div className="welcome-sidebar-support-card">
            <strong>Need help?</strong>
            <p>Our support team is here to assist you.</p>
            <button
              type="button"
              className="welcome-sidebar-support-button"
              onClick={() => setContactOpen(true)}
            >
              Contact Support
            </button>
          </div>
          <button
            type="button"
            className="welcome-sidebar-link welcome-sidebar-button welcome-sidebar-logout"
            onClick={handleLogout}
          >
            Logout
          </button>
        </aside>

        <section className="welcome-content">
          <div className="welcome-body-grid">
            <div className="welcome-main-panel" id="upload-bank-statements">
              {/*
              Preview screen UI disabled by request.
              {showPreviewScreen ? (
                <div className="welcome-preview-panel">
                  <div className="welcome-preview-header">
                    <div className="welcome-preview-topline">
                      <div>
                        <h2>Statement Preview</h2>
                        <p>
                          {activePreviewJob.preview.fileName}
                          {activePreviewJob.preview.sheetName ? ` - ${activePreviewJob.preview.sheetName}` : ""}
                        </p>
                      </div>
                      <div className="welcome-preview-actions">
                        <button
                          type="button"
                          className="welcome-secondary-button"
                          onClick={() => setActivePreviewId(null)}
                        >
                          Back To Files
                        </button>
                        {activePreviewJob.preview.downloadUrl && (
                          <a
                            href={activePreviewJob.preview.downloadUrl}
                            className="welcome-primary-button welcome-download-button"
                          >
                            Download Excel
                          </a>
                        )}
                      </div>
                    </div>
                    {completedJobs.length > 1 && (
                      <div className="welcome-preview-select-row">
                        <select
                          className="welcome-preview-select"
                          value={activePreviewId || ""}
                          onChange={(event) => setActivePreviewId(event.target.value)}
                        >
                          {completedJobs.map((job) => (
                            <option key={job.localId} value={job.localId}>
                              {job.fileName}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="welcome-preview-table-wrap">
                    <table className="welcome-preview-table">
                      <thead>
                        <tr>
                          {activePreviewJob.preview.headers.map((header, index) => (
                            <th key={`${header}-${index}`}>{header || `Column ${index + 1}`}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {activePreviewJob.preview.rows.map((row, rowIndex) => (
                          <tr key={`row-${rowIndex}`}>
                            {row.map((cell, cellIndex) => (
                              <td key={`cell-${rowIndex}-${cellIndex}`}>{cell}</td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {activePreviewJob.preview.truncated && (
                    <p className="welcome-preview-note">
                      Showing the first part of the statement sheet here. Use Download Excel for the full file.
                    </p>
                  )}
                </div>
              ) : (
              */}
                <>
                  <div className="welcome-heading-row">
                    <div>
                      <h1>StatementIQ Application</h1>
                    </div>
                  </div>

                  <div className="welcome-dashboard-grid">
                    <section className="welcome-workflow-card welcome-bank-card">
                      <div className="welcome-card-heading">
                        <h2>1. Select Bank</h2>
                        <p>Choose your bank from the list below</p>
                      </div>
                      <div className="welcome-bank-search">
                        <input
                          id="welcome-bank-search"
                          type="text"
                          placeholder="Search and select your bank..."
                          value={bankSearch}
                          onChange={(event) => setBankSearch(event.target.value)}
                        />
                      </div>
                      <strong className="welcome-bank-list-title">Supported Banks</strong>
                      <div className="welcome-bank-list">
                        <div className="welcome-bank-options" role="listbox" aria-label="Banks">
                          {filteredBankOptions.length > 0 ? (
                            filteredBankOptions.map((bank) => (
                              <button
                                key={bank.name}
                                type="button"
                                className={`welcome-bank-option ${
                                  selectedBankName === bank.name ? "active" : ""
                                }`}
                                onClick={() => setSelectedBankName(bank.name)}
                                aria-selected={selectedBankName === bank.name}
                                role="option"
                              >
                                <span className="welcome-bank-name">{bank.name}</span>
                                {selectedBankName === bank.name && (
                                  <span className="welcome-bank-selected-label">Selected</span>
                                )}
                              </button>
                            ))
                          ) : (
                            <span className="welcome-bank-empty">No banks found</span>
                          )}
                        </div>
                      </div>
                    </section>

                    <section className="welcome-workflow-card welcome-upload-card-panel">
                      <div className="welcome-card-heading">
                        <h2>2. Upload Statement PDF</h2>
                        <p>Upload your bank statement in PDF format</p>
                      </div>
                      <div
                        className={`welcome-upload-dropzone ${dragActive ? "drag-active" : ""}`}
                        onDragEnter={handleDragEnter}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".pdf"
                          aria-label="Upload PDF statement"
                          className="welcome-hidden-input"
                          onChange={handleFileSelection}
                        />
                        <div className="welcome-upload-icon" aria-hidden="true">
                          <img src={UPLOAD_PDF_ICON_URL} alt="" />
                        </div>
                        <button
                          type="button"
                          className="welcome-primary-button welcome-choose-pdf-button"
                          onClick={handleOpenPicker}
                          disabled={loading}
                        >
                          Choose PDF File
                        </button>
                        {selectedFiles.length > 0 && (
                          <strong className="welcome-selected-file-count">
                            {selectedFiles[0].name}
                          </strong>
                        )}
                      </div>
                      <div className="welcome-upload-info-strip">
                        <span>PDF files only</span>
                        <span>Max size: {MAX_PDF_PAGES} pages</span>
                        <span>Secure upload</span>
                      </div>
                    </section>

                    <section className="welcome-workflow-card welcome-processing-card">
                      <div className="welcome-card-heading">
                        <h2>3. Start Processing</h2>
                        <p>Review your selections and start the conversion</p>
                      </div>
                      <div className="welcome-processing-summary">
                        <div className="welcome-processing-summary-row">
                          <div>
                            <span>Selected Bank</span>
                            <strong>{selectedBankName || "Select bank"}</strong>
                          </div>
                        </div>
                        <div className="welcome-processing-summary-row">
                          <div>
                            <span>File Name</span>
                            <strong>{selectedPdfFile?.name || "Upload PDF"}</strong>
                          </div>
                        </div>
                        <div className="welcome-processing-summary-row">
                          <div>
                            <span>File Size</span>
                            <strong>{selectedPdfFile ? formatFileSize(selectedPdfFile.size) : "-"}</strong>
                          </div>
                        </div>
                        <div className="welcome-processing-summary-row">
                          <div>
                            <span>Pages</span>
                            <strong>{processingTotalPages ? formatPageCount(processingTotalPages) : "-"}</strong>
                          </div>
                        </div>
                        <div className="welcome-processing-summary-row">
                          <div>
                            <span>Status</span>
                            <strong>{processingStatusLabel}</strong>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="welcome-primary-button welcome-start-processing-button"
                        onClick={handleStartProcess}
                        disabled={loading || !selectedBankName || !jobs.length}
                      >
                        {!loading && <span className="welcome-start-icon" aria-hidden="true" />}
                        {loading ? "Processing..." : "Start Processing"}
                      </button>
                      {loading && (
                        <button
                          type="button"
                          className="welcome-secondary-button welcome-cancel-button"
                          onClick={handleCancelProcess}
                        >
                          Cancel
                        </button>
                      )}
                      <div className="welcome-processing-page-status">
                        <strong>{processingPageMessage}</strong>
                      </div>
                      {completedJobs.length > 0 && (
                        <button
                          type="button"
                          className="welcome-success-download-button"
                          onClick={() => handleDownloadExcel(completedJobs[0])}
                        >
                          <svg
                            className="welcome-download-icon"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path
                              d="M12 3v11m0 0 4-4m-4 4-4-4M5 19h14"
                              fill="none"
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                            />
                          </svg>
                          Download
                        </button>
                      )}
                      {errorMessage && (
                        <div className="welcome-processing-status">
                          <p className="welcome-upload-error">{errorMessage}</p>
                        </div>
                      )}
                    </section>

                    <section className="welcome-workflow-card welcome-filter-step">
                      <div className="welcome-step-section-header">
                        <div>
                          <h2>4. Generate Filtered Sheet</h2>
                          <p>Create a filtered Excel sheet with custom criteria.</p>
                        </div>
                      </div>

                      <div className="welcome-xlsx-upload-box">
                        <input
                          ref={xlsxInputRef}
                          type="file"
                          accept=".xlsx"
                          className="welcome-hidden-input"
                          onChange={handleXlsxSelection}
                        />
                        <button
                          type="button"
                          className="welcome-secondary-button welcome-xlsx-upload-button"
                          onClick={handleOpenXlsxPicker}
                        >
                          Upload XLSX
                        </button>
                        <span className="welcome-xlsx-file-name">
                          {selectedXlsxFile ? selectedXlsxFile.name : "No XLSX file uploaded"}
                        </span>
                      </div>

                      <button
                        type="button"
                        className="welcome-primary-button welcome-generate-filter-button"
                        onClick={handleGenerateFilteredExcel}
                        disabled={!selectedXlsxFile || filterLoading}
                      >
                        {filterLoading ? "Generating..." : "Generate Filter Excel Sheet"}
                      </button>
                    </section>
                  </div>
                </>
              {/* )} */}
            </div>
          </div>
        </section>
      </main>

      {tipsOpen && (
        <div className="welcome-tips-popup-overlay" onClick={() => setTipsOpen(false)}>
          <div className="welcome-tips-popup" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="welcome-tips-close"
              aria-label="Close tips"
              onClick={() => setTipsOpen(false)}
            >
              ×
            </button>

            <div className="welcome-tips-header">
              <h2>Tips for Better Results</h2>
            </div>

            <ul className="welcome-tips-list">
              <li>Upload original bank statement PDFs whenever possible for the cleanest extraction.</li>
              <li>Keep statement pages in the correct order and avoid mixing multiple accounts in one file.</li>
              <li>Use clear scans only when the original PDF is unavailable, and make sure text is upright.</li>
              <li>Do not crop out balances, dates, or transaction descriptions before uploading.</li>
              <li>For large jobs, separate different statements into their own PDFs for more reliable results.</li>
              <li>Uploading one PDF at a time can often give faster processing and better results.</li>
            </ul>

            <div className="welcome-tips-note">
              These steps help improve accuracy and keep debit, credit, and balance columns aligned correctly.
            </div>
          </div>
        </div>
      )}

      {contactOpen && (
        <div className="welcome-tips-popup-overlay" onClick={() => setContactOpen(false)}>
          <div className="welcome-tips-popup" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="welcome-tips-close"
              aria-label="Close contact popup"
              onClick={() => setContactOpen(false)}
            >
              ×
            </button>

            <div className="welcome-tips-header">
              <h2>Contact Us</h2>
            </div>

            <div className="welcome-contact-body">
              <p>
                If you need help with bank statement uploads, conversion issues, or account access,
                please contact our support team.
              </p>
              <p>
                Include your name, company, and a short summary of the issue so we can help quickly.
              </p>
              <p>
                <a href="mailto:support@statementiq.co.uk">support@statementiq.co.uk</a>
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default WelcomePage;
